import { Box, Chip, Divider, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import type { GrantedLine } from "./permissionsPanelModel";

interface Props {
  title: string;
  subHeader?: string;
  /** Which clients the pages show, e.g. "Every HR Services client". */
  appliesTo?: string;
  granted: GrantedLine[];
  missing: string[];
  /** Shown under the No Access line. */
  footnote?: string;
}

/** "Permissions Granted": one line per page a user opens, then the pages they do
 * not. Every access panel renders through it, so they all read the same. */
export default function PermissionsPanel({
  title,
  subHeader,
  appliesTo,
  granted,
  missing,
  footnote,
}: Props) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "grey.50",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#185059" }}>
        {title}
      </Typography>
      {subHeader && (
        <Typography variant="body2" color="text.secondary">
          {subHeader}
        </Typography>
      )}
      {appliesTo && (
        <Box>
          <Chip
            size="small"
            variant="outlined"
            label={`Applies to: ${appliesTo}`}
            sx={{
              color: "#185059",
              borderColor: "#185059",
              fontWeight: 600,
              height: "auto",
              maxWidth: "100%",
              "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 },
            }}
          />
        </Box>
      )}
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
        {granted.map((line) => (
          <Box
            component="li"
            key={line.key}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              py: 0.25,
            }}
          >
            <Check fontSize="small" sx={{ color: "#185059", mt: "2px" }} />
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 700, color: "#185059" }}>
                {line.name}
              </Box>
              {" - "}
              {line.grants}
            </Typography>
          </Box>
        ))}
        {granted.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No pages.
          </Typography>
        )}
      </Box>
      {missing.length > 0 && (
        <>
          <Divider />
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>
              No Access:
            </Box>{" "}
            {missing.join(", ")}
          </Typography>
          {footnote && (
            <Typography variant="caption" color="text.secondary">
              {footnote}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
